import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo, { getAbsoluteUrl } from "@/components/feature/Seo";
import { brand } from "@/mocks/siteData";
import { buildReviewSchema } from "@/lib/reviewSchema";
import { usePublicReviews } from "@/hooks/usePublicCatalog";
import ReviewsHero from "./components/ReviewsHero";
import ReviewsSummary from "./components/ReviewsSummary";
import ReviewPhotoWall from "./components/ReviewPhotoWall";
import ReviewsList from "./components/ReviewsList";
import ReviewsShareForm from "./components/ReviewsShareForm";
import ReviewsCta from "./components/ReviewsCta";

export default function Reviews() {
  const { data: reviews, loading, error, reload } = usePublicReviews();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const reviewSchema = buildReviewSchema(reviews, {
    name: brand.fullName,
    url: getAbsoluteUrl("/reviews"),
    image: reviews[0]?.photos?.[0] || reviews[0]?.avatar || undefined,
  });

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={`Visitor Reviews & Photos | ${brand.name} Agri Tourism`}
        description="Real reviews and visit photos from guests who have experienced farm and village life with Rythugaru across Konaseema, Andhra Pradesh."
        path="/reviews"
        keywords="Rythugaru reviews, agri tourism reviews, farm experience reviews India, Konaseema visitor reviews, guest photos"
        image="https://readdy.ai/api/search-image?query=Happy%20visitors%20walking%20through%20green%20paddy%20fields%20on%20a%20farm%20in%20rural%20India%2C%20warm%20golden%20sunlight%2C%20lush%20green%20tones%2C%20candid%20documentary%20style%2C%20ultra%20detailed&width=1920&height=1080&seq=reviews-list-cover&orientation=landscape"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Visitor Reviews & Photos",
            description: "Guest reviews and photos from farm and village experiences in Konaseema.",
            inLanguage: "en",
          },
          ...(reviewSchema ? [reviewSchema] : []),
        ]}
      />
      <Navbar />
      <main>
        <ReviewsHero reviews={reviews} />
        <ReviewsSummary reviews={reviews} />
        {loading && reviews.length === 0 ? (
          <section className="bg-background-50 py-24">
            <div className="w-full px-4 md:px-8 flex flex-col items-center gap-4 text-foreground-600">
              <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
              <p className="text-sm">Loading reviews…</p>
            </div>
          </section>
        ) : reviews.length === 0 ? (
          <section className="bg-background-50 py-24">
            <div className="w-full px-4 md:px-8 text-center max-w-lg mx-auto">
              <h3 className="font-heading text-2xl text-foreground-950 font-semibold">
                We couldn't load the reviews
              </h3>
              <p className="mt-3 text-foreground-600 leading-relaxed">
                {error || "Please try again in a moment."}
              </p>
              <button
                type="button"
                onClick={reload}
                className="mt-7 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Try again
              </button>
            </div>
          </section>
        ) : (
          <>
            <ReviewPhotoWall reviews={reviews} />
            <ReviewsList reviews={reviews} />
          </>
        )}
        <ReviewsShareForm />
        <ReviewsCta />
      </main>
      <Footer />
    </div>
  );
}